import {
  convexQuery,
  useConvexAuth,
  useConvexMutation,
} from "@convex-dev/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { ConvexError } from "convex/values";
import { Id } from "@cvx/_generated/dataModel";
import { api } from "~/convex/_generated/api";
import { useAuthActions } from "@convex-dev/auth/react";
import { useState } from "react";

export const Route = createFileRoute("/_app/invites/$inviteId/")({
  component: InvitePage,
});

function InvitePage() {
  const { inviteId } = Route.useParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string>();
  const { isAuthenticated, isLoading } = useConvexAuth();
  const { signIn } = useAuthActions();

  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));
  const {
    data: invites,
    isError,
    error: invitesError,
  } = useQuery(convexQuery(api.invites.getAppFounderInvitesForUser, {}));

  const invite = invites?.find((invite) => invite._id === inviteId);

  const { mutateAsync: acceptOrRejectInvite, isPending } = useMutation({
    mutationFn: useConvexMutation(api.invites.acceptOrRejectAppFounderInvite),
  });

  const handleResponse = async (status: "accepted" | "rejected") => {
    try {
      await acceptOrRejectInvite({
        inviteId: inviteId as Id<"appFounderInvites">,
        status,
      });

      if (status === "accepted") {
        navigate({ to: "/login" });
      } else {
        setError("Thank you for your response. You can close this window.");
      }
    } catch (err) {
      if (err instanceof ConvexError) {
        setError(err.data);
      } else {
        setError("An unexpected error occurred. Please try again later.");
      }
    }
  };

  if (!isAuthenticated && !isLoading) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
        <div className="w-full max-w-md rounded-lg border border-border bg-card p-6">
          <h1 className="mb-6 text-center text-2xl font-semibold text-primary">
            Team Invitation
          </h1>
          <p className="mb-8 text-center text-sm text-primary/60">
            Please sign in to accept your team invitation.
          </p>
          <Button
            variant="outline"
            className="w-full gap-2 bg-transparent"
            onClick={() =>
              signIn("google", { redirectTo: window.location.pathname })
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 24 24"
            >
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading || !user) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
        <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 text-center">
          <p className="text-sm text-primary/60">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || isError || !invite) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
        <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 text-center">
          {user && (
            <div className="flex flex-col items-center gap-4">
              <img
                src={user?.image}
                alt={user?.name}
                className="h-16 w-16 rounded-full"
              />
              <div className="flex flex-col items-center gap-1">
                <h3 className="font-medium text-primary">{user?.name}</h3>
                <p className="text-sm text-primary/60">{user?.email}</p>
              </div>
            </div>
          )}

          <p className="mt-6 text-sm text-primary/60">
            {error ||
              invitesError?.message ||
              "Could not find invite, please check your link or contact support"}
          </p>

          {user && (
            <Button
              variant="outline"
              className="mt-6"
              onClick={() => navigate({ to: "/dashboard" })}
            >
              Go back to dashboard
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6">
        <h1 className="mb-6 text-center text-2xl font-semibold text-primary">
          Team Invitation
        </h1>
        <p className="mb-8 text-center text-sm text-primary/60">
          You've been invited to join {invite?.appName} as a team member. Would
          you like to accept this invitation?
        </p>
        <div className="flex justify-center gap-4">
          <Button
            variant="outline"
            onClick={() => handleResponse("rejected")}
            disabled={isPending}
          >
            Decline
          </Button>
          <Button
            onClick={() => handleResponse("accepted")}
            disabled={isPending}
          >
            {isPending ? "Processing..." : "Accept"}
          </Button>
        </div>
      </div>
    </div>
  );
}
